import { FormControl, Grid, InputLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import { MprcButton } from "components";
import { IconButtonDelete } from "components/iconButtons";
import { MprcConfirmDialog } from "components/MprcConfirmDialog";
import { MprcContainer } from "components/MprcContainer";
import { CustomFieldValueDto, UserEditProfileResponse } from "generated/CoreApi_v1";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CustomFieldInput, getCustomFieldGridSize } from "views/foundation/customField/components/CustomFieldInput";
import { EditUserRecordFiles } from "../EditUser/EditUserRecordFiles";
////////////////////////////////////////////////////////////////////////////

interface IValidationFields {
    customFields: { [key: string]: boolean; };
}

export interface IPostValidation {
    customFields: { [key: string]: string[] | undefined; };
}

interface IProps {
    viewData: UserEditProfileResponse;
    viewErrors: ApiProblemErrors | undefined;
    onFormUpdate: (dto: UserEditProfileResponse) => void;
    onFormValidation: (valid: boolean) => void;
    onSubForm: (subform: any) => void;
}

////////////////////////////////////////////////////////////////////////////

///
const getDefaultValidate = (viewData: UserEditProfileResponse): IValidationFields => {
    var obj = { customFields: {} } as IValidationFields;
    viewData.customFieldsAdditional
        ?.filter((x) => x.isRequired)
        .forEach((f) => (obj.customFields[f.fieldId] = f.fieldValue != undefined && f.fieldValue.toString().length > 0));

    return obj;
};

////////////////////////////////////////////////////////////////////////////

/**
 * Edição de Usuário - Informações Adicionais
 */
export const EditUserAdditionalInfoBox: React.FC<IProps> = (props) => {
    ///////////////////////////////////////////////////////////////////////////

    const { t } = useTranslation();
    const { viewData, viewErrors, onFormUpdate, onFormValidation, onSubForm } = props;

    const [dialog, setDialog] = useState<any>(undefined);
    const [formValidation, setFormValidation] = useState<IValidationFields>(getDefaultValidate(viewData));
    const [postValidation, setPostValidation] = useState<IPostValidation>({ customFields: {} });
    const [concealedFields, setConcealedFields] = useState<CustomFieldValueDto[]>(viewData.customFieldsConcealed?.filter((x) => x.fieldValue) ?? []);

    const inputRef = useRef<any>(undefined);

    ////////////////////////////////////////////////////////////////////////////
    ///
    const handleUpdateRecord = (name: keyof UserEditProfileResponse, v: any) => {
        onFormUpdate({ ...viewData, [name]: v });
    };

    ///
    const handleUpdateCustomField = (field: CustomFieldValueDto, value: any, isValid: boolean) => {
        let newCustomFields = viewData.customFieldsAdditional?.map((item) => {
            if (item.fieldId === field.fieldId) item.fieldValue = value;
            return item;
        });

        handleUpdateRecord("customFieldsAdditional", newCustomFields);

        if ((field.isRequired && field.fieldValue != undefined) || formValidation.customFields[field.fieldId] === false)
            setFormValidation({
                ...formValidation,
                customFields: { ...formValidation.customFields, [field.fieldId]: isValid }
            });
    };

    ///
    const handleUpdateConcealedField = (fieldId: string, value: any) => {
        let newConcealedFields = viewData.customFieldsConcealed?.map((item) => {
            if (item.fieldId === fieldId) {
                item.fieldValue = value;
            }
            return item;
        });

        handleUpdateRecord("customFieldsConcealed", newConcealedFields);
    };

    ///
    const handleAddConcealedField = (fieldId: string) => {
        var item = viewData.customFieldsConcealed?.find((x) => x.fieldId === fieldId) ?? undefined;
        if (item) setConcealedFields([...concealedFields, item]);

        //Focus no input adicionado e remove a referência após o uso para ser utilizado pelo próximo field adicionado
        setTimeout(() => {
            inputRef.current?.focus();
            inputRef.current = undefined;
        }, 50);
    };

    ///
    const handleRemoveConcealedField = (fieldId: string) => {
        setDialog(
            <MprcConfirmDialog
                open
                severity="danger"
                message={t("User.Edit.ConfirmDeleteConcealedFieldMessage")}
                onClose={(result) => {
                    if (result === true) {
                        setConcealedFields(concealedFields.filter((x) => x.fieldId != fieldId));
                        handleUpdateConcealedField(fieldId, null);
                    }

                    setDialog(null);
                }}
            />
        );
    };

    ////////////////////////////////////////////////////////////////////////////

    //Inicializa os campos escamoteados com o que vem do viewdata quando concealedFields for vazio (ainda não inicializado)
    useEffect(() => {
        if (concealedFields.length === 0) {
            setConcealedFields(viewData.customFieldsConcealed?.filter((x) => x.fieldValue) ?? []);
        }
    }, [viewData.customFieldsConcealed]);

    //Ao atualizar o estado do formValidation
    useEffect(() => {
        if (Object.keys(formValidation.customFields).length === 0) {
            onFormValidation(true); //Se a tab não tiver campos custom obrigatórios, a validação retorna True
        } else {
            onFormValidation(Object.values(formValidation.customFields).filter((x) => x === false).length === 0);
        }
    }, [formValidation]);

    useEffect(() => {
        var fields = (viewData.customFieldsAdditional ?? []).concat(viewData.customFieldsConcealed ?? []);
        if (viewErrors != null && fields.length > 0) {

            const postValidationFields: { [key: string]: string[]; } = {};
            setFormValidation(() => {

                var customFields = fields.reduce(
                    (accumulator, currValue) => {
                        if (viewErrors.hasOwnProperty(`customFields.${currValue.fieldId}`)) {
                            accumulator[currValue.fieldId] = false;
                            postValidationFields[currValue.fieldId] = viewErrors[`customFields.${currValue.fieldId}`];
                        }
                        return accumulator;
                    }, {} as { [key: string]: boolean; }) ?? {};

                return { customFields: customFields };
            }
            );

            setPostValidation(({ customFields: postValidationFields }));
        }
    }, [viewErrors]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    return (
        <>
            {dialog}

            <Stack direction="column" gap={2}>
                {(viewData.permissions.allowAddRecordFiles || viewData.permissions.allowViewRecordFiles) &&
                    <div>
                        <MprcContainer margin="dense">
                            <Typography variant="overline" component="p">{t("User.Edit.RecordFilesSubtitle")}</Typography>
                        </MprcContainer>
                        <MprcButton
                            title={t("User.Edit.RecordFilesButtonTooltip")}
                            text={t("User.Edit.RecordFilesButtonText")}
                            variant="contained"
                            rounded
                            onClick={() =>
                                onSubForm(
                                    <EditUserRecordFiles
                                        userId={viewData.userId}
                                        userName={viewData.name}
                                        editMode="profile"
                                        onClose={() => onSubForm(null)}
                                        canDeleteFiles={viewData.permissions.allowDeleteRecordFiles}
                                        canAddFiles={viewData.permissions.allowAddRecordFiles}
                                    />
                                )
                            }
                        />
                    </div>
                }
                {
                    (
                        (viewData.customFieldsAdditional ?? []).length > 0
                        || (viewData.customFieldsConcealed ?? []).length > 0
                        || concealedFields.length > 0
                    ) &&
                    <div>

                        <MprcContainer>
                            <Typography variant="overline">{t("User.Edit.OtherInfoSubtitle")}</Typography>
                        </MprcContainer>

                        {
                            (viewData.customFieldsAdditional ?? []).length > 0 &&
                            (
                                <MprcContainer>
                                    <Grid container rowSpacing={1} columnSpacing={4}>
                                        {viewData.customFieldsAdditional!.map((item, i) => (
                                            <Grid key={i} size={{ xs: getCustomFieldGridSize(item.showWidth) }}>
                                                <CustomFieldInput
                                                    field={item}
                                                    onChange={(value, formattedValue, isValid) => handleUpdateCustomField(item, value, isValid)}
                                                    error={postValidation.customFields[item.fieldId]}
                                                    validateOnFirstMount={true}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </MprcContainer>
                            )
                        }

                        {
                            (viewData.customFieldsConcealed ?? []).length > 0 &&
                            (
                                <MprcContainer>
                                    <FormControl fullWidth>
                                        <InputLabel>{t("User.Edit.SelectCustomFieldLabel")}</InputLabel>
                                        <Select defaultValue="" value="" onChange={(e) => handleAddConcealedField(e.target.value as string)}>
                                            {viewData.customFieldsConcealed
                                                ?.filter((item) => concealedFields.findIndex((x) => x.fieldId === item.fieldId) === -1)
                                                .map((item) => (
                                                    <MenuItem key={item.fieldId} value={item.fieldId}>
                                                        {item.label}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </MprcContainer>
                            )
                        }

                        {
                            concealedFields.length > 0 &&
                            (
                                <MprcContainer>
                                    <Grid container rowSpacing={1}>
                                        {concealedFields.map((item, i) => (
                                            <Grid key={i} container justifyContent="space-between" alignItems="end" wrap="nowrap">
                                                <Grid size={{ xs: getCustomFieldGridSize(item.showWidth) }}>
                                                    <CustomFieldInput
                                                        ref={inputRef}
                                                        field={item}
                                                        onChange={(value) => handleUpdateConcealedField(item.fieldId, value)}
                                                        error={postValidation.customFields[item.fieldId]}
                                                        validateOnFirstMount={true}
                                                    />
                                                </Grid>
                                                <Grid size="grow">
                                                    <IconButtonDelete size="small" onClick={() => handleRemoveConcealedField(item.fieldId)} />
                                                </Grid>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </MprcContainer>
                            )
                        }
                    </div>
                }
            </Stack>
        </>
    );
};
